// MaterialPreview — a material read in place, pure and READ-ONLY.
//
// The library detail pane's material view (A1) and the reusable reading face
// for any surface that shows a material without editing it (e.g. the upcoming
// generation side view). A pure projection: content + recorded provenance +
// (optional) quality in, intents out. No state, no I/O, no edit affordances —
// `MaterialDocument` gets NO `on*` callback, which is its read-only mode.
//
// Layout, calm and borderless (progressive disclosure):
//   - a quiet header like a source's: the title, then one dim facts line —
//     the kind as plain text (no chip), the AI marker (only when the
//     provenance says an agent authored it), "Created from" the lineage
//     source · the creation date;
//   - the quality verdict folded behind a disclosure (present only once a
//     quality read has landed — absent is honest silence, not a fake verdict);
//   - the document body in its reading register.

import { Trans, useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import type { MaterialProvenance } from "../../app/ports/resource.js";
import { AiMarker } from "../AiMarker/AiMarker.js";
import {
    InlineLink,
    selectInlineLinkView,
} from "../Chat/InlineLink/InlineLink.js";
import { MaterialDocument } from "../Editor/MaterialDocument/MaterialDocument.js";
import { defaultRenderers } from "../Editor/renderers.js";
import type { Element } from "../Editor/types.js";
import { EmptyState } from "../EmptyState/EmptyState.js";
import { Heading } from "../Heading/Heading.js";
import { Icon } from "../Icon/Icon.js";
import type { ResourceRef } from "../Navigation/types.js";
import {
    type MaterialQuality,
    QualitySummary,
} from "../QualitySummary/QualitySummary.js";
import { Text } from "../Text/Text.js";
import {
    type FriendlyTitles,
    materialPreviewHeader,
} from "./materialPreviewModel.js";
import "./MaterialPreview.css";

export type MaterialPreviewProps = {
    readonly title: string;
    readonly elements: readonly Element[];
    readonly provenance: MaterialProvenance;
    // The material-level quality read. Absent ⇒ no quality section (not yet
    // loaded, or unavailable) — never a guessed verdict.
    readonly quality?: MaterialQuality;
    // Following the "Created from" lineage. Absent ⇒ the source titles read as
    // plain text.
    readonly onOpenSource?: (source: ResourceRef) => void;
};

// A material's quiet header, shared by this reading face and the library's
// editing face: the title (with an optional action cluster beside it, e.g.
// the editor's toolbar), then one dim facts line — the kind as plain text,
// the AI marker when an agent authored it, "Created from" the lineage
// source(s), and the creation date. `children` rides under the facts.
export const MaterialHeader = ({
    title,
    provenance,
    onOpenSource,
    friendlyTitles,
    action,
    children,
}: {
    readonly title: string;
    readonly provenance: MaterialProvenance;
    readonly onOpenSource?: (source: ResourceRef) => void;
    // Friendlier names for the lineage chips (a source's own title).
    readonly friendlyTitles?: FriendlyTitles;
    readonly action?: ReactNode;
    readonly children?: ReactNode;
}): ReactNode => {
    const { t, i18n } = useLingui();
    const header = materialPreviewHeader(title, provenance, friendlyTitles);
    const createdOn = header.createdOn?.toLocaleDateString(i18n.locale, {
        day: "numeric",
        month: "short",
        year: "numeric",
    });
    return (
        <header className="bilda-material-preview__head">
            <div className="bilda-material-preview__title">
                <Heading
                    level={2}
                    size="2xl"
                    weight="regular"
                    className="bilda-heading--clamp"
                    title={header.title}
                >
                    {header.title}
                </Heading>
                {action}
            </div>
            <div className="bilda-material-preview__origin">
                <Text as="span" size="sm" tone="dim">
                    {t({ message: "Material", context: "resource kind" })}
                </Text>
                {header.aiAuthored ? (
                    <AiMarker
                        variant="chip"
                        title={t`This material was drafted with AI. Review it before you rely on it.`}
                    />
                ) : null}
                {header.derivedFrom.length > 0 ? (
                    <Text as="span" size="sm" tone="dim">
                        {`· ${t`Created from`}`}
                    </Text>
                ) : null}
                {/* Each source as the app's one reference chip, opening it
                    (in the library) — plain text when nothing can open it. */}
                {header.derivedFrom.map((ref) => (
                    <InlineLink
                        key={ref.id}
                        view={selectInlineLinkView(
                            { type: "resource", ref },
                            onOpenSource,
                        )}
                    >
                        <Text as="span" size="sm" tone="dim">
                            {ref.title}
                        </Text>
                    </InlineLink>
                ))}
                {createdOn !== undefined ? (
                    <Text as="span" size="sm" tone="dim">
                        {`· ${createdOn}`}
                    </Text>
                ) : null}
            </div>
            {children}
        </header>
    );
};

export const MaterialPreview = ({
    title,
    elements,
    provenance,
    quality,
    onOpenSource,
}: MaterialPreviewProps): ReactNode => {
    const { t } = useLingui();
    return (
        <article className="bilda-material-preview">
            <MaterialHeader
                title={title}
                provenance={provenance}
                onOpenSource={onOpenSource}
            >
                {quality !== undefined ? (
                    <details className="bilda-material-preview__quality">
                        <summary>
                            <Text as="span" size="sm" tone="dim">
                                {t`Quality`}
                            </Text>
                        </summary>
                        <QualitySummary
                            quality={quality}
                            ariaLabel={t`Quality of this material`}
                        />
                    </details>
                ) : null}
            </MaterialHeader>
            {elements.length > 0 ? (
                <MaterialDocument
                    elements={elements}
                    renderers={defaultRenderers}
                    ariaLabel={t`Material content`}
                />
            ) : (
                <div className="bilda-material-preview__empty">
                    <EmptyState
                        icon={<Icon name="material" size="lg" />}
                        title={<Trans>This material is empty</Trans>}
                    />
                </div>
            )}
        </article>
    );
};
